/**
 * Integration Pattern Card Component
 *
 * Displays a collapsed view of an integration pattern with key information
 * and visual indicators. Expandable to show full details.
 */

import React from 'react';
import {
  Network,
  ChevronDown,
  ChevronRight,
  Zap,
  Package,
  Calendar,
  Circle,
  ArrowRight,
} from 'lucide-react';

interface IntegrationPatternCardProps {
  pattern: {
    pattern_id: string;
    pattern_name: string;
    pattern_type: string;
    business_value?: string;
    data_flow?: string;
    complexity?: string;
    frequency?: string;
    source_modules?: string[] | string;
    target_modules?: string[] | string;
  };
  isExpanded: boolean;
  onToggle: () => void;
}

const IntegrationPatternCard: React.FC<IntegrationPatternCardProps> = ({
  pattern,
  isExpanded,
  onToggle,
}) => {
  // Parse JSON arrays if they're strings
  const parseModules = (modules: string[] | string | undefined): string[] => {
    if (!modules) return [];
    if (Array.isArray(modules)) return modules;
    try {
      return JSON.parse(modules);
    } catch {
      return [];
    }
  };

  const sourceModules = parseModules(pattern.source_modules);
  const targetModules = parseModules(pattern.target_modules);

  // Get complexity badge color
  const getComplexityColor = (complexity?: string) => {
    switch (complexity?.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    }
  };

  // Get frequency icon and label
  const getFrequencyDisplay = (frequency?: string) => {
    switch (frequency?.toLowerCase()) {
      case 'real_time':
        return { icon: Zap, label: 'Real-time', color: 'text-purple-600' };
      case 'batch':
        return { icon: Package, label: 'Batch', color: 'text-blue-600' };
      case 'daily':
        return { icon: Calendar, label: 'Daily', color: 'text-indigo-600' };
      case 'on_demand':
        return { icon: Circle, label: 'On-demand', color: 'text-teal-600' };
      default:
        return { icon: Circle, label: frequency || 'Unknown', color: 'text-neutral-600' };
    }
  };

  const frequencyDisplay = getFrequencyDisplay(pattern.frequency);
  const FrequencyIcon = frequencyDisplay.icon;

  // Get pattern type display
  const getPatternTypeDisplay = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Truncate text for preview
  const truncateText = (text: string | undefined, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg hover:shadow-md transition-shadow">
      {/* Collapsed Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-6 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
      >
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="p-2.5 bg-purple-50 rounded-lg flex-shrink-0">
            <Network className="w-6 h-6 text-purple-600" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-4 mb-2">
              <h4 className="text-lg font-semibold text-neutral-900">
                {pattern.pattern_name}
              </h4>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-neutral-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-neutral-400 flex-shrink-0" />
              )}
            </div>

            {/* Pattern Type Badge */}
            <div className="mb-3">
              <span className="inline-block px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                {getPatternTypeDisplay(pattern.pattern_type)}
              </span>
            </div>

            {/* Quick Stats Row */}
            <div className="flex items-center gap-4 mb-3 text-sm">
              {/* Frequency */}
              <div className={`flex items-center gap-1.5 ${frequencyDisplay.color}`}>
                <FrequencyIcon className="w-4 h-4" />
                <span className="font-medium">{frequencyDisplay.label}</span>
              </div>

              {/* Complexity */}
              {pattern.complexity && (
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded border ${getComplexityColor(
                    pattern.complexity
                  )}`}
                >
                  {pattern.complexity.charAt(0).toUpperCase() + pattern.complexity.slice(1)}{' '}
                  Complexity
                </span>
              )}

              {/* Module Flow */}
              {sourceModules.length > 0 && targetModules.length > 0 && (
                <div className="flex items-center gap-1.5 text-neutral-600">
                  <span className="text-xs font-medium">{sourceModules.join(', ')}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="text-xs font-medium">{targetModules.join(', ')}</span>
                </div>
              )}
            </div>

            {/* Business Value Preview */}
            {!isExpanded && pattern.business_value && (
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {truncateText(pattern.business_value, 150)}
                </p>
              </div>
            )}
          </div>
        </div>
      </button>
    </div>
  );
};

export default IntegrationPatternCard;
